/* eslint-disable react-hooks/rules-of-hooks */
import { ColumnType } from "../../../../../../shared/infrastructure/ui/components/libs/table/types";
import { FilterByDate } from "../../../../../../shared/infrastructure/ui/components/libs/table/filter-by-date";
import { FilterSelectCarriers } from "../../../../../../shared/infrastructure/ui/components/libs/table/filter-select-carriers";
import { FilterSelectStatus } from "../../../../../../shared/infrastructure/ui/components/libs/table/filter-select-status";
import { FilterSelectUsers } from "../../../../../../shared/infrastructure/ui/components/libs/table/filter-select-users";
import { FilterSelectUsersApprover } from "../../../../../../shared/infrastructure/ui/components/libs/table/filter-select-users-approver";
import { NegotiationColumn } from "./components/negotiations-column";
import { SortByHeader } from "../../../../../../shared/infrastructure/ui/components/libs/table/sort-by-header";
import { StatusFormat } from "../../components/status-format";
import { buildHighwayUrl } from "../../utils/helpers";
import { getLocationFormat } from "../../../../../../old/utils/parseData";
import {
  CurrencyFormat,
  DateFormat,
} from "../../../../../../shared/infrastructure/ui/components/libs/table/formats";
import { FaCheck, FaTimes } from "react-icons/fa";
import {
  LinkIcon,
  PinIcon,
  Tooltip,
  TruckIcon,
  WarningIcon,
} from "../../../../../../shared/infrastructure";
import { LocationT, OfferT, UseGetOffersFilters } from "../../../../domain";
import {
  OrderTypes,
  SortByType,
  SortByValuesType,
} from "../../../../../../shared/domain/entities/fetch-entities";
import React, { useMemo, useState } from "react";

const useRepViewTableColumns = (
  defaultFilters: UseGetOffersFilters = {}
): {
  logsTableColumns: Array<ColumnType>;
  sortByValues: SortByValuesType;
  filters: UseGetOffersFilters;
} => {
  const [sortByValues, setSortByValues] = useState<SortByValuesType>({
    sort_by: SortByType.CREATED_AT,
    order_by: OrderTypes.DESC,
  });

  const [filters, setFilters] = useState<UseGetOffersFilters>(defaultFilters);

  const handleSortByValue = (value: SortByType): void => {
    setSortByValues((prevState) => ({
      ...prevState,
      sort_by: value,
      order_by:
        prevState.sort_by !== value
          ? OrderTypes.ASC
          : prevState.order_by === OrderTypes.ASC
          ? OrderTypes.DESC
          : OrderTypes.ASC,
    }));
  };

  const handleFilters =
    (name: string): ((value: string | Array<string> | undefined) => void) =>
    (value) => {
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    };

  const logsTableColumns = useMemo(
    (): Array<ColumnType> => [
      {
        prop: "all",
        width: 10,
        header: <span></span>,
        component: {
          component: NegotiationColumn,
        },
      },
      {
        prop: "created_at",
        width: 140,
        header: (
          <SortByHeader
            headerText="Posted Date"
            onChange={() => handleSortByValue(SortByType.CREATED_AT)}
            value={sortByValues}
            headerKey={SortByType.CREATED_AT}
          />
        ),
        component: {
          component: DateFormat,
          props: {
            // format: "MM/DD/YYYY HH:mm",
            format: "MMM DD, hh:mm A",
            style: {
              padding: 5,
              fontSize: 12,
            },
          },
        },
      },
      {
        prop: "all",
        width: 130,
        maxWidth: 130,
        header: (
          <FilterSelectUsers
            movement_number={filters.movement_number ?? ""}
            headerText="Posted By"
            onFilter={handleFilters("posted_by")}
          />
        ),
        component: {
          component: ({ data }: { data?: OfferT }): React.ReactElement => (
            <Tooltip text={data?.posted_by_user?.name ?? ""}>
              <span className="max-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap capitalize text-[#000000]">
                {data?.posted_by_user?.name ?? "- -"}
              </span>
            </Tooltip>
          ),
        },
      },
      {
        prop: "all",
        width: 165,
        header: (
          <FilterSelectStatus
            headerText="Status"
            onFilter={handleFilters("status")}
          />
        ),
        component: {
          component: StatusFormat,
        },
      },
      {
        prop: "all",
        width: 130,
        maxWidth: 130,
        header: (
          <FilterSelectUsersApprover
            movement_number={filters.movement_number ?? ""}
            headerText="Approver"
            onFilter={handleFilters("approver")}
          />
        ),
        component: {
          component: ({ data }: { data?: OfferT }): React.ReactElement => (
            <Tooltip text={data?.approver_user?.name ?? ""}>
              <span className="max-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap">
                {data?.approver_user?.name ?? "- -"}
              </span>
            </Tooltip>
          ),
        },
      },
      {
        prop: "status_change_date",
        width: 150,
        header: (
          <FilterByDate
            headerText="Change Date"
            onFilter={handleFilters("status_change_date")}
          />
        ),
        component: {
          component: DateFormat,
          props: {
            format: "MM/DD/YYYY HH:mm",
            style: {
              padding: 5,
              fontSize: 12,
            },
          },
        },
      },
      {
        prop: "truck_location",
        width: 130,
        maxWidth: 130,
        header: (
          <section className="flex flex-row items-center justify-center">
            <span className="mr-[10px] text-[11px]">Truck Location</span>
            <section className="h-[14px] w-[14px] items-center justify-center">
              <PinIcon width={12} height={12} fill="black" />
            </section>
          </section>
        ),
        component: {
          component: ({ data }: { data?: LocationT }): React.ReactElement => (
            <Tooltip
              text={data ? getLocationFormat(data) : ""}
              // classes={{ container: "max-w-[130px]" }}
            >
              <span className="max-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap capitalize text-black">
                {getLocationFormat(data)}
              </span>
            </Tooltip>
          ),
        },
      },
      {
        prop: "is_empty_truck",
        width: 50,
        header: (
          <section className="flex flex-row items-center justify-center">
            <span className="mr-[10px] text-[11px]">Empty</span>
            <section className="h-[14px] w-[14px] items-center justify-center">
              <TruckIcon width={12} height={12} fill="black" />
            </section>
          </section>
        ),
        component: {
          component: ({ data }: { data?: boolean }): React.ReactElement => (
            <section className="flex justify-center">
              {data ? <FaCheck className="text-[#6296F8]" /> : <FaTimes />}
            </section>
          ),
        },
      },
      {
        prop: "all",
        width: 130,
        maxWidth: 130,
        header: (
          <FilterSelectCarriers
            movement_number={filters.movement_number ?? ""}
            headerText="Carrier"
            onFilter={handleFilters("carrier")}
          />
        ),
        component: {
          component: ({ data }: { data?: OfferT }): React.ReactElement => (
            <section className="grid grid-cols-[auto_auto] items-center">
              <LinkIcon width={14} height={14} fill="#3C7CF3" />
              <span className="font-[400]">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={buildHighwayUrl(data?.carrier?.highway_id || "")}
                  className="grid cursor-pointer content-start"
                >
                  <span className="flex max-w-[130px] flex-row items-center justify-start gap-[8px] capitalize text-[#3C7CF3]">
                    {data?.carrier?.status !== "I" && (
                      <Tooltip text="Inactive Carrier">
                        {" "}
                        <WarningIcon fill="#FFBD2E" />
                      </Tooltip>
                    )}
                    <Tooltip text={data?.carrier?.name ?? ""}>
                      <span className="max-w-[130px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {data?.carrier?.name?.toLowerCase() ?? "- -"}
                      </span>
                    </Tooltip>
                  </span>
                </a>
              </span>
            </section>
          ),
        },
      },
      {
        prop: "rate",
        header: (
          <SortByHeader
            headerText="Rate"
            onChange={() => handleSortByValue(SortByType.RATE)}
            value={sortByValues}
            headerKey={SortByType.RATE}
          />
        ),
        align: "right",
        width: 90,
        component: {
          component: ({ data }: { data?: number }): React.ReactElement => (
            <span className="grid grid-flow-col items-center gap-[8px] whitespace-nowrap text-[#000000]">
              <CurrencyFormat data={data} />
            </span>
          ),
        },
      },
    ],
    [filters.movement_number, sortByValues]
  );

  return { logsTableColumns, sortByValues, filters };
};

export { useRepViewTableColumns };
